import { TFunction } from 'i18next';
import { number, object, string } from 'yup';

import {
  isValidAddressLine,
  isValidAllowedCurrency,
  isValidBirthNumber,
  isValidBirthDateDdMmYyyy,
  isValidDateDdMmYyyy,
  isValidDocumentNumber,
  isValidEmail,
  isValidIco,
  isValidLettersOnly,
  isValidPartnerNumber,
  isValidPersonName,
  REQUIRED_PERSON_NAME_MIN_LENGTH,
  isValidPhoneDigits,
  isValidPhonePrefix,
  isValidPostalCode,
  isValidSigningTime,
  isValidVerificationCode,
} from './rules';

function testWhenPresent(message: string, validate: (value: string) => boolean) {
  return string().test('format', message, (value) => {
    if (!value) {
      return true;
    }
    return validate(value);
  });
}

export function requiredString(t: TFunction) {
  return string().required(t('form.required'));
}

export function requiredEmailField(t: TFunction) {
  return requiredString(t).test('email', t('form.emailInvalid'), (value) => isValidEmail(value ?? ''));
}

export function optionalPersonNameField(t: TFunction) {
  return string()
    .nullable()
    .notRequired()
    .test('format', t('form.personNameInvalid'), (value) => {
      if (value === null || value === undefined || value === '') {
        return true;
      }
      return isValidPersonName(value);
    });
}

export function requiredPersonNameField(t: TFunction) {
  return requiredString(t)
    .test('personNameFormat', function (value) {
      if (isValidPersonName((value ?? '').trim(), 1)) {
        return true;
      }
      return this.createError({ message: t('form.personNameInvalid') });
    })
    .test('personNameMinLength', function (value) {
      const trimmed = (value ?? '').trim();
      if (!isValidPersonName(trimmed, 1)) {
        return true;
      }
      if (isValidPersonName(trimmed, REQUIRED_PERSON_NAME_MIN_LENGTH)) {
        return true;
      }
      return this.createError({ message: t('form.personNameMinLength') });
    });
}

export function requiredLettersOnlyField(t: TFunction) {
  return requiredString(t).test('lettersOnly', t('form.lettersOnly'), (value) => isValidLettersOnly(value ?? ''));
}

export function requiredPostalCodeField(t: TFunction) {
  return requiredString(t).test('postalCode', t('form.postalCodeInvalid'), (value) => isValidPostalCode(value ?? ''));
}

export function requiredDateDdMmYyyyField(t: TFunction) {
  return requiredString(t)
    .test('dateFormat', t('form.dateFormat'), (value) => /^\d{1,2}\.\d{1,2}\.\d{4}$/.test((value ?? '').trim()))
    .test('dateInvalid', t('form.dateInvalid'), (value) => isValidDateDdMmYyyy(value ?? ''));
}

export function requiredBirthDateDdMmYyyyField(t: TFunction) {
  return requiredString(t)
    .test('dateFormat', t('form.dateFormat'), (value) => /^\d{1,2}\.\d{1,2}\.\d{4}$/.test((value ?? '').trim()))
    .test('birthDateInvalid', function (value) {
      if (isValidBirthDateDdMmYyyy(value ?? '')) {
        return true;
      }
      return this.createError({ message: t('form.birthDateInvalid') });
    });
}

export function requiredSigningTimeField(t: TFunction) {
  return requiredString(t).test('signingTime', t('form.signingTimeInvalid'), (value) =>
    isValidSigningTime(value ?? ''),
  );
}

export function requiredVerificationCodeField(t: TFunction) {
  return requiredString(t).test('verificationCode', t('form.verificationCodeInvalid'), (value) =>
    isValidVerificationCode(value ?? ''),
  );
}

export function requiredBirthNumberField(t: TFunction) {
  return requiredString(t).test('birthNumber', t('form.birthNumberInvalid'), (value) =>
    isValidBirthNumber(value ?? ''),
  );
}

export function requiredIcoField(t: TFunction) {
  return requiredString(t).test('ico', t('form.icoInvalid'), (value) => isValidIco(value ?? ''));
}

export function requiredNbsRegistrationNumberField(t: TFunction) {
  return requiredString(t).test('nbsRegistrationNumber', t('form.nbsRegistrationNumberInvalid'), (value) =>
    isValidPartnerNumber(value ?? ''),
  );
}

export function requiredPartnerNumberField(t: TFunction) {
  return requiredString(t).test('partnerNumber', t('form.partnerNumberInvalid'), (value) =>
    isValidPartnerNumber(value ?? ''),
  );
}

export function requiredDocumentNumberField(t: TFunction) {
  return requiredString(t).test('documentNumber', t('form.documentNumberInvalid'), (value) =>
    isValidDocumentNumber(value ?? ''),
  );
}

export function requiredAddressLineField(t: TFunction) {
  return requiredString(t).test('addressLine', t('form.addressLineInvalid'), (value) =>
    isValidAddressLine(value ?? ''),
  );
}

export function requiredAllowedCurrencyField(t: TFunction) {
  return requiredString(t).test('allowedCurrency', t('form.allowedCurrencyInvalid'), (value) =>
    isValidAllowedCurrency(value ?? ''),
  );
}

export function requiredPhoneStringField(t: TFunction) {
  return requiredString(t).test('phone', t('form.phoneInvalid'), (value) => isValidPhoneDigits(value ?? ''));
}

export function requiredPhoneObjectField(t: TFunction) {
  return object().shape({
    prefix: string()
      .required(t('form.required'))
      .test('phonePrefix', t('form.phoneInvalid'), (value) => isValidPhonePrefix(value ?? '')),
    number: string()
      .required(t('form.required'))
      .test('phoneNumber', t('form.phoneInvalid'), (value) => isValidPhoneDigits(value ?? '')),
  });
}

export function requiredPositiveNumberField(t: TFunction) {
  return number()
    .typeError(t('form.numberInvalid'))
    .required(t('form.required'))
    .test('positiveNumber', t('form.positiveNumberInvalid'), (value) => (value ?? 0) > 0);
}

export function requiredNonNegativeNumberField(t: TFunction) {
  return number()
    .typeError(t('form.numberInvalid'))
    .required(t('form.required'))
    .min(0, t('form.nonNegativeNumberInvalid'));
}

export function requiredMaxTwoDigitNumberField(t: TFunction) {
  return number()
    .typeError(t('form.numberInvalid'))
    .required(t('form.required'))
    .integer(t('form.maxTwoDigitNumberInvalid'))
    .min(0, t('form.maxTwoDigitNumberInvalid'))
    .max(99, t('form.maxTwoDigitNumberInvalid'));
}
